import { normalizePageResponse } from "../lib/pagination";
import { imsGet, imsPost, imsPut, imsPatch, imsDel, hrmsGet } from "../api/api";
import sessionDB from "../utils/sessionDB";

/**
 * Product Vendor service — IMS inventory module.
 * Endpoints: /saas/ims/product-vendors/*
 */

const SEARCH = "IMS02S";
const ADD = "IMS02A";
const UPDATE = "IMS02U";
const DELETE = "IMS02D";

const DEFAULT_PAGE = { page: 0, size: 10, sortBy: "legalCompanyName", sortDir: "ASC" };

/** Share in-flight identical GETs (helps React Strict Mode double-mount). */
const inflightRequests = new Map();

function dedupeAsync(key, factory) {
  const existing = inflightRequests.get(key);
  if (existing) return existing;
  const promise = Promise.resolve()
    .then(factory)
    .finally(() => {
      if (inflightRequests.get(key) === promise) inflightRequests.delete(key);
    });
  inflightRequests.set(key, promise);
  return promise;
}

/**
 * Fixed Vendor Type values for the Add/Edit dropdown and filters.
 * Payload uses the label string (e.g. "Manufacturer"), never the id.
 */
export const SUPPLIER_TYPES = [
  { id: 1, label: "Manufacturer" },
  { id: 2, label: "OEM" },
  { id: 3, label: "Distributor" },
];

/** Dropdown options: value === label string sent as supplierType. */
export const VENDOR_TYPE_OPTIONS = SUPPLIER_TYPES.map((t) => ({
  value: t.label,
  label: t.label,
}));

/** supplierTypeId → label learned from API responses (search/detail mapping). */
const supplierTypeRegistry = new Map();

function rememberSupplierType(id, name) {
  if (id == null || id === "" || !name) return;
  supplierTypeRegistry.set(String(id), String(name));
}

function unwrapData(response) {
  if (response == null) return response;
  if (response.data !== undefined) {
    if (
      response.data &&
      typeof response.data === "object" &&
      !Array.isArray(response.data) &&
      response.data.data !== undefined
    ) {
      return response.data.data;
    }
    return response.data;
  }
  return response;
}

function unwrapPage(response, params) {
  return normalizePageResponse(response, params ? { ...DEFAULT_PAGE, ...params } : { paginate: false });
}
function unwrapItem(response) {
  const data = unwrapData(response);
  if (Array.isArray(data)) return data[0] || null;
  return data && typeof data === "object" ? data : null;
}

function extractCount(response) {
  const data = unwrapData(response);
  if (typeof data === "number") return data;
  if (typeof data?.count === "number") return data.count;
  if (typeof data?.total === "number") return data.total;
  if (typeof data?.totalElements === "number") return data.totalElements;
  const n = Number(data);
  return Number.isFinite(n) ? n : 0;
}

function firstDefined(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

function toNumberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function emptyToNull(value) {
  if (value === undefined || value === null) return null;
  const s = String(value).trim();
  return s === "" ? null : s;
}

export function toApiVendorStatus(uiStatus) {
  // Create/Update body enum: Active | InActive | Blacklisted
  const lower = String(uiStatus || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (lower === "blacklisted") return "Blacklisted";
  if (lower === "inactive") return "InActive";
  return "Active";
}

/** Query-param form used by search/count endpoints (Active | InActive | Blacklisted). */
export function toSearchVendorStatus(uiStatus) {
  return toApiVendorStatus(uiStatus);
}

export function toUiVendorStatus(apiStatus) {
  const lower = String(apiStatus || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (lower === "blacklisted") return "Blacklisted";
  if (lower === "inactive") return "Inactive";
  return "Active";
}

function normalizeRiskLevelToken(value) {
  let raw = value;
  if (raw && typeof raw === "object") {
    raw = firstDefined(
      raw.value,
      raw.name,
      raw.label,
      raw.code,
      raw.riskLevel,
      raw.risk_level,
      raw.level
    );
  }
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

export function toApiRiskLevel(uiRisk) {
  // Create/Update body enum: Low | Medium | High
  const token = normalizeRiskLevelToken(uiRisk);
  if (token === "3" || token.includes("high")) return "High";
  if (token === "2" || token.includes("medium") || token === "med") return "Medium";
  return "Low";
}

/** Query-param form used by risk-level search (docs use lowercase). */
export function toSearchRiskLevel(uiRisk) {
  return toApiRiskLevel(uiRisk).toLowerCase();
}

export function toUiRiskLevel(apiRisk) {
  const token = normalizeRiskLevelToken(apiRisk);
  if (!token) return "Low";
  if (token === "3" || token.includes("high")) return "High";
  if (token === "2" || token.includes("medium") || token === "med") return "Medium";
  return "Low";
}

function resolveCreatedBy() {
  const empId = sessionDB.getEmpId?.();
  if (empId !== undefined && empId !== null && empId !== "") {
    const asNum = Number(empId);
    return Number.isNaN(asNum) ? String(empId) : asNum;
  }
  const emp = sessionDB.getEmpObj?.() || {};
  const fallback =
    emp.emp_id ??
    emp.empId ??
    emp.employeeId ??
    emp.employee_id ??
    emp.id ??
    null;
  if (fallback == null || fallback === "") return null;
  const asNum = Number(fallback);
  return Number.isNaN(asNum) ? String(fallback) : asNum;
}

export function resolveSupplierTypeLabel(supplierTypeId, supplierTypeName) {
  if (supplierTypeName) {
    rememberSupplierType(supplierTypeId, supplierTypeName);
    return supplierTypeName;
  }
  const known = supplierTypeRegistry.get(String(supplierTypeId));
  if (known) return known;
  const match = SUPPLIER_TYPES.find((t) => String(t.id) === String(supplierTypeId));
  return match?.label || "";
}

export function resolveSupplierTypeId(typeLabelOrId) {
  if (typeLabelOrId === "" || typeLabelOrId == null) return null;
  const asNum = Number(typeLabelOrId);
  if (!Number.isNaN(asNum) && String(typeLabelOrId).trim() !== "") {
    const byId = SUPPLIER_TYPES.find((t) => t.id === asNum);
    if (byId) return byId.id;
    if (/^\d+$/.test(String(typeLabelOrId).trim())) return asNum;
  }
  const label = String(typeLabelOrId).trim().toLowerCase();
  for (const [id, name] of supplierTypeRegistry.entries()) {
    if (String(name).trim().toLowerCase() === label) return Number(id);
  }
  const byLabel = SUPPLIER_TYPES.find((t) => t.label.toLowerCase() === label);
  return byLabel ? byLabel.id : null;
}

/**
 * Map API vendor entity → UI shape used by catalog / detail / form.
 */
export function mapApiVendorToUi(api) {
  if (!api || typeof api !== "object") return null;

  const id = firstDefined(api.vendorId, api.vendor_id, api.id);
  const supplierTypeId = firstDefined(api.supplierTypeId, api.supplier_type_id);
  const supplierTypeName = firstDefined(
    api.supplierTypeName,
    api.supplier_type_name,
    api.supplierType,
    api.supplier_type,
    api.vendorType,
    api.type
  );
  const typeLabel = resolveSupplierTypeLabel(supplierTypeId, supplierTypeName);
  const approvedById = firstDefined(api.approvedBy, api.approved_by);

  const isAuthorizedRaw = firstDefined(
    api.isAuthorizedDealer,
    api.is_authorized_dealer,
    api.isAuthorized,
    api.authorizedDealer,
    api.authorisedDealer
  );
  let isAuthorized = "";
  if (isAuthorizedRaw === true || isAuthorizedRaw === "Yes" || isAuthorizedRaw === "YES" || isAuthorizedRaw === "true") {
    isAuthorized = "Yes";
  } else if (
    isAuthorizedRaw === false ||
    isAuthorizedRaw === "No" ||
    isAuthorizedRaw === "NO" ||
    isAuthorizedRaw === "false"
  ) {
    isAuthorized = "No";
  }

  const docs = firstDefined(api.documents, api.uploadedFiles, api.kycDocuments, api.kyc_documents) || [];
  const uploadedFiles = Array.isArray(docs)
    ? docs.map((d, idx) =>
        typeof d === "string"
          ? { id: `doc-${idx}`, name: d, uploadedAt: "" }
          : {
              id: d.id || d.documentId || `doc-${idx}`,
              name: d.name || d.fileName || d.documentName || "Document",
              uploadedAt: d.uploadedAt || d.uploadDate || "",
            }
      )
    : [];

  return {
    id: id != null ? String(id) : undefined,
    vendorId: id,
    tradeName: firstDefined(api.tradeName, api.trade_name) || "",
    legalCompanyName: firstDefined(api.legalCompanyName, api.legal_company_name) || "",
    type: typeLabel,
    supplierTypeId: supplierTypeId != null ? String(supplierTypeId) : "",
    companyRegNumber: firstDefined(
      api.companyRegistrationNumber,
      api.company_registration_number,
      api.companyRegNumber,
      api.company_reg_number
    ) || "",
    contactPerson: firstDefined(
      api.primaryContactName,
      api.primary_contact_name,
      api.contactPerson,
      api.contact_person
    ) || "",
    phone: firstDefined(
      api.primaryMobileNumber,
      api.primary_mobile_number,
      api.primaryPhone,
      api.phone
    ) || "",
    email: firstDefined(
      api.primaryEmailAddress,
      api.primary_email_address,
      api.email
    ) || "",
    secondaryName: firstDefined(
      api.secondaryContactName,
      api.secondary_contact_name,
      api.secondaryName
    ) || "",
    secondaryMobile: firstDefined(
      api.secondaryMobileNumber,
      api.secondary_mobile_number,
      api.secondaryMobile
    ) || "",
    secondaryEmail: firstDefined(
      api.secondaryEmailAddress,
      api.secondary_email_address,
      api.secondaryEmail
    ) || "",
    alternatePhone: firstDefined(
      api.officePhone,
      api.alternatePhoneNumber,
      api.alternate_phone_number,
      api.alternatePhone
    ) || "",
    addressLine1: firstDefined(
      api.registeredAddressLine1,
      api.registered_address_line1,
      api.addressLine1,
      api.address_line1
    ) || "",
    addressLine2: firstDefined(
      api.registeredAddressLine2,
      api.registered_address_line2,
      api.addressLine2,
      api.address_line2
    ) || "",
    addressCity: firstDefined(
      api.registeredCity,
      api.registered_city,
      api.city,
      api.addressCity
    ) || "",
    addressState: firstDefined(
      api.registeredState,
      api.registered_state,
      api.state,
      api.addressState
    ) || "",
    addressPincode: firstDefined(
      api.registeredPincode,
      api.registered_pincode,
      api.pincode,
      api.pinCode,
      api.addressPincode
    ) || "",
    address: firstDefined(api.address, api.registeredAddress) || "",
    taxId: firstDefined(api.gstin, api.gstIn, api.taxId, api.tax_id) || "",
    pan: firstDefined(api.pan, api.panNumber, api.pan_number) || "",
    msmeRegNo: firstDefined(
      api.msmeRegistrationNumber,
      api.msme_registration_number,
      api.msmeRegNo,
      api.udyamNumber
    ) || "",
    cin: firstDefined(api.cin, api.cinNumber, api.cin_number) || "",
    tan: firstDefined(api.tan, api.tanNumber, api.tan_number) || "",
    iec: firstDefined(api.iec, api.iecNumber, api.iec_number) || "",
    isoCertifications:
      firstDefined(
        api.isoCertifications,
        api.iso_certifications,
        api.certifications,
        api.isoCertification
      ) || "",
    bankName: firstDefined(api.bankName, api.bank_name, api.bankNameBranch) || "",
    accountNumber: firstDefined(api.accountNumber, api.account_number, api.bankAccountNumber) || "",
    ifscCode: firstDefined(api.ifscCode, api.ifsc_code) || "",
    swiftCode: firstDefined(api.swiftCode, api.swift_code) || "",
    upiId: firstDefined(api.upiId, api.upi_id, api.upi) || "",
    description: firstDefined(
      api.productCategories,
      api.productsServicesOffered,
      api.products_services_offered,
      api.description,
      api.productsOffered
    ) || "",
    isAuthorized,
    paymentTerms: firstDefined(
      api.paymentTerms,
      api.payment_terms,
      api.standardPaymentTerms
    ) || "",
    creditPeriod: toNumberOrNull(
      firstDefined(
        api.creditPeriodDays,
        api.credit_period_days,
        api.creditPeriod,
        api.standardCreditPeriod
      )
    ),
    currency: firstDefined(api.currencyCode, api.currency) || "INR",
    uploadedFiles,
    status: toUiVendorStatus(
      firstDefined(
        api.vendorStatus,
        api.vendor_status,
        api.status,
        api.vendorStatusName,
        api.vendor_status_name
      )
    ),
    approvedBy: approvedById != null ? String(approvedById) : "",
    approvedByName:
      firstDefined(api.approvedByName, api.approved_by_name, api.approverName) || "",
    approvalDate: firstDefined(api.approvalDate, api.approval_date) || "",
    riskLevel: toUiRiskLevel(
      firstDefined(
        api.riskLevel,
        api.risk_level,
        api.vendorRiskLevel,
        api.vendor_risk_level,
        api.riskLevelName,
        api.risk_level_name,
        api.risk
      )
    ),
    raw: api,
  };
}

/**
 * Map UI form fields → API create/update body (ProductVendorCreateRequest / UpdateRequest).
 * Single mapping boundary for Add + Edit — do not re-map in page components.
 */
export function mapFormToApiPayload(form) {
  // Wire supplierType as the selected label string (Manufacturer | OEM | Distributor), never an id.
  const supplierType = emptyToNull(form.vendorType || form.type || form.supplierType);

  const createdByRaw =
    form.createdBy != null && form.createdBy !== "" ? form.createdBy : resolveCreatedBy();
  const createdBy = toNumberOrNull(createdByRaw) ?? createdByRaw;

  const isAuthorizedDealer =
    form.isAuthorized === "Yes" || form.isAuthorized === true
      ? true
      : form.isAuthorized === "No" || form.isAuthorized === false
        ? false
        : null;

  const documents = Array.isArray(form.uploadedFiles)
    ? form.uploadedFiles
        .map((f) => (typeof f === "string" ? f : f?.name || f?.fileName || f?.documentName))
        .map((name) => (name != null ? String(name).trim() : ""))
        .filter(Boolean)
    : [];

  return {
    legalCompanyName: emptyToNull(form.legalCompanyName),
    tradeName: emptyToNull(form.tradeName),
    supplierType,
    companyRegistrationNumber: emptyToNull(form.companyRegNumber),

    primaryContactName: emptyToNull(form.primaryName || form.contactPerson),
    primaryMobileNumber: emptyToNull(form.primaryMobile || form.phone),
    primaryEmailAddress: emptyToNull(form.primaryEmail || form.email),

    secondaryContactName: emptyToNull(form.secondaryName),
    secondaryMobileNumber: emptyToNull(form.secondaryMobile),
    secondaryEmailAddress: emptyToNull(form.secondaryEmail),

    officePhone: emptyToNull(form.alternatePhone),

    registeredAddressLine1: emptyToNull(form.addressLine1),
    registeredAddressLine2: emptyToNull(form.addressLine2),
    registeredCity: emptyToNull(form.addressCity),
    registeredState: emptyToNull(form.addressState),
    registeredCountry: null,
    registeredPincode: emptyToNull(form.addressPincode),

    // No branch-address fields in the UI.
    branchAddressLine1: null,
    branchAddressLine2: null,
    branchCity: null,
    branchState: null,
    branchCountry: null,
    branchPincode: null,

    gstin: emptyToNull(form.gstin || form.taxId),
    pan: emptyToNull(form.pan),
    msmeRegistrationNumber: emptyToNull(form.msmeRegNo),
    cin: emptyToNull(form.cin),
    tan: emptyToNull(form.tan),
    iec: emptyToNull(form.iec),
    isoCertifications: emptyToNull(form.isoCertifications),

    bankName: emptyToNull(form.bankName),
    bankBranch: null,
    accountName: null,
    accountNumber: emptyToNull(form.accountNumber),
    ifscCode: emptyToNull(form.ifscCode),
    swiftCode: emptyToNull(form.swiftCode),
    upiId: emptyToNull(form.upiId),

    productCategories: emptyToNull(form.productsOffered || form.description),
    isAuthorizedDealer,
    paymentTerms: emptyToNull(form.paymentTerms),
    creditPeriodDays: toNumberOrNull(form.creditPeriod),
    currencyCode: emptyToNull(form.currency) || "INR",
    documents: documents.length > 0 ? documents : null,

    vendorStatus: toApiVendorStatus(form.vendorStatus || form.status || "Active"),
    approvedBy: toNumberOrNull(form.approvedBy),
    approvalDate: emptyToNull(form.approvalDate),
    riskLevel: toApiRiskLevel(form.riskLevel || "Low"),
    createdBy,
  };
}

function buildPageQuery({ page, size, sortBy, sortDir } = {}) {
  const p = {
    page: page ?? DEFAULT_PAGE.page,
    size: size ?? DEFAULT_PAGE.size,
    sortBy: sortBy ?? DEFAULT_PAGE.sortBy,
    sortDir: sortDir ?? DEFAULT_PAGE.sortDir,
  };
  return `page=${p.page}&size=${p.size}&sortBy=${encodeURIComponent(p.sortBy)}&sortDir=${encodeURIComponent(p.sortDir)}`;
}

function mapPageToUi(page) {
  const content = (page.content || []).map(mapApiVendorToUi).filter(Boolean);
  return { ...page, content, vendors: content };
}

/** GET /product-vendors/search */
export async function searchVendors(params = {}) {
  const response = await imsGet(
    `/product-vendors/search?${buildPageQuery({ ...params, sortBy: params.sortBy || "vendorId" })}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response, params));
}

/**
 * Session overlay for commercial/KYC fields the create/update response (and often
 * GET-by-id) omits even when the request body included them.
 */
const VENDOR_FIELD_OVERLAY_KEY = "product_vendor_field_overlay";

function readVendorFieldOverlays() {
  try {
    return JSON.parse(sessionStorage.getItem(VENDOR_FIELD_OVERLAY_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function pickVendorFieldOverlay(uiVendor) {
  if (!uiVendor) return null;
  return {
    isoCertifications: uiVendor.isoCertifications || "",
    isAuthorized: uiVendor.isAuthorized || "",
    paymentTerms: uiVendor.paymentTerms || "",
    creditPeriod:
      uiVendor.creditPeriod !== undefined && uiVendor.creditPeriod !== null
        ? uiVendor.creditPeriod
        : null,
    uploadedFiles: Array.isArray(uiVendor.uploadedFiles) ? uiVendor.uploadedFiles : [],
  };
}

function saveVendorFieldOverlay(vendorId, uiVendor) {
  const id = vendorId != null ? String(vendorId) : "";
  if (!id) return;
  const overlay = pickVendorFieldOverlay(uiVendor);
  if (!overlay) return;
  try {
    const all = readVendorFieldOverlays();
    all[id] = overlay;
    sessionStorage.setItem(VENDOR_FIELD_OVERLAY_KEY, JSON.stringify(all));
  } catch {
    /* ignore quota / private mode */
  }
}

function applyVendorFieldOverlay(mapped) {
  if (!mapped?.id) return mapped;
  const overlay = readVendorFieldOverlays()[String(mapped.id)];
  if (!overlay) return mapped;
  return {
    ...mapped,
    isoCertifications: mapped.isoCertifications || overlay.isoCertifications || "",
    isAuthorized: mapped.isAuthorized || overlay.isAuthorized || "",
    paymentTerms: mapped.paymentTerms || overlay.paymentTerms || "",
    creditPeriod:
      mapped.creditPeriod !== undefined && mapped.creditPeriod !== null
        ? mapped.creditPeriod
        : overlay.creditPeriod,
    uploadedFiles:
      mapped.uploadedFiles && mapped.uploadedFiles.length > 0
        ? mapped.uploadedFiles
        : overlay.uploadedFiles || [],
  };
}

/** GET /product-vendors/{vendorId} */
export async function fetchVendorById(vendorId) {
  const response = await imsGet(`/product-vendors/${vendorId}`, SEARCH);
  return applyVendorFieldOverlay(mapApiVendorToUi(unwrapItem(response)));
}

/** GET /product-vendors/search/trade-name */
export async function searchVendorsByTradeName(tradeName, params = {}) {
  const response = await imsGet(
    `/product-vendors/search/trade-name?tradeName=${encodeURIComponent(tradeName)}&${buildPageQuery(params)}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response, params));
}

/** GET /product-vendors/search/name */
export async function searchVendorsByLegalName(legalCompanyName, params = {}) {
  const response = await imsGet(
    `/product-vendors/search/name?legalCompanyName=${encodeURIComponent(legalCompanyName)}&${buildPageQuery(params)}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response, params));
}

/** GET /product-vendors/search/email */
export async function searchVendorsByEmail(primaryEmailAddress, params = {}) {
  const response = await imsGet(
    `/product-vendors/search/email?primaryEmailAddress=${encodeURIComponent(primaryEmailAddress)}&${buildPageQuery(params)}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response, params));
}

/** GET /product-vendors/search/status */
export async function searchVendorsByStatus(vendorStatus, params = {}) {
  const status = toSearchVendorStatus(vendorStatus);
  const qs = buildPageQuery(params);

  return dedupeAsync(`vendorsByStatus:${status}:${qs}`, async () => {
    const response = await imsGet(
      `/product-vendors/search/status?vendorStatus=${encodeURIComponent(status)}&${qs}`,
      SEARCH
    );
    return mapPageToUi(unwrapPage(response, params));
  });
}

/** GET /product-vendors/search/risk-level */
export async function searchVendorsByRiskLevel(riskLevel, params = {}) {
  const wanted = toUiRiskLevel(riskLevel);
  const qs = buildPageQuery(params);
  const encodings = Array.from(
    new Set([toSearchRiskLevel(riskLevel), toApiRiskLevel(riskLevel), wanted.toUpperCase()])
  );

  return dedupeAsync(`vendorsByRisk:${wanted}:${qs}`, async () => {
    let lastResult = {
      content: [],
      vendors: [],
      totalElements: 0,
      totalPages: 0,
      number: 0,
      size: 0,
    };

    for (const risk of encodings) {
      try {
        const response = await imsGet(
          `/product-vendors/search/risk-level?riskLevel=${encodeURIComponent(risk)}&${qs}`,
          SEARCH
        );
        const mapped = mapPageToUi(unwrapPage(response, params));
        lastResult = mapped;
        const list = mapped.vendors || mapped.content || [];
        if (list.length === 0) continue;
        const allMatch = list.every((v) => toUiRiskLevel(v.riskLevel) === wanted);
        if (allMatch) return mapped;
      } catch {
        /* try next encoding (lowercase / PascalCase / UPPERCASE) */
      }
    }

    return lastResult;
  });
}

/**
 * Risk Level dropdown via GET /product-vendors/search/risk-level?riskLevel=low|medium|high
 */
export async function fetchRiskLevelDropdownOptions({ size = 10 } = {}) {
  return dedupeAsync(`riskLevelDropdown:${size}`, async () => {
    const probes = ["low", "medium", "high"];
    const byValue = new Map();

    await Promise.all(
      probes.map(async (probe) => {
        try {
          const page = await searchVendorsByRiskLevel(probe, {
            page: 0,
            size,
            sortBy: "legalCompanyName",
            sortDir: "ASC",
          });
          const ui = toUiRiskLevel(probe);
          byValue.set(ui, { value: ui, label: ui });
          (page.vendors || page.content || []).forEach((v) => {
            if (v?.riskLevel) {
              const fromVendor = toUiRiskLevel(v.riskLevel);
              byValue.set(fromVendor, { value: fromVendor, label: fromVendor });
            }
          });
        } catch {
          /* skip unsupported risk level */
        }
      })
    );

    const order = { Low: 0, Medium: 1, High: 2 };
    return Array.from(byValue.values()).sort(
      (a, b) => (order[a.value] ?? 99) - (order[b.value] ?? 99)
    );
  });
}

/** GET /product-vendors/search/supplier-type */
export async function searchVendorsBySupplierType(supplierTypeId, params = {}) {
  const response = await imsGet(
    `/product-vendors/search/supplier-type?supplierTypeId=${encodeURIComponent(supplierTypeId)}&${buildPageQuery(params)}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response, params));
}

/**
 * Vendor Type dropdown options — fixed labels only (no API).
 * Values: Manufacturer | OEM | Distributor.
 */
export async function fetchSupplierTypeDropdownOptions() {
  return VENDOR_TYPE_OPTIONS;
}

/**
 * Vendor Status options via GET /product-vendors/search/status?vendorStatus=Active|…
 */
export async function fetchVendorStatusDropdownOptions({ size = 10 } = {}) {
  return dedupeAsync(`vendorStatusDropdown:${size}`, async () => {
    const probes = ["Active", "InActive", "Blacklisted"];
    const byValue = new Map();

    await Promise.all(
      probes.map(async (probe) => {
        try {
          const page = await searchVendorsByStatus(probe, {
            page: 0,
            size,
            sortBy: "legalCompanyName",
            sortDir: "ASC",
          });
          const ui = toUiVendorStatus(probe);
          byValue.set(ui, { value: ui, label: ui });
          (page.vendors || []).forEach((v) => {
            if (v?.status) byValue.set(v.status, { value: v.status, label: v.status });
          });
        } catch {
          /* skip unsupported status */
        }
      })
    );

    const order = { Active: 0, Inactive: 1, Blacklisted: 2 };
    return Array.from(byValue.values()).sort(
      (a, b) => (order[a.value] ?? 99) - (order[b.value] ?? 99)
    );
  });
}

function currencyLabel(code) {
  try {
    const name = new Intl.DisplayNames(undefined, { type: "currency" }).of(code);
    return name && name.toUpperCase() !== code ? `${code} (${name})` : code;
  } catch {
    return code;
  }
}

/**
 * Currency options built from the currencyCode values the vendor API returns.
 * Falls back to the ISO 4217 list the runtime exposes when no vendor has one yet.
 */
export async function fetchCurrencyDropdownOptions({ size = 100 } = {}) {
  return dedupeAsync(`currencyDropdown:${size}`, async () => {
    const codes = new Set();
    try {
      const page = await searchVendors({ page: 0, size, sortBy: "vendorId", sortDir: "ASC" });
      (page.vendors || []).forEach((v) => {
        const code = String(firstDefined(v.raw?.currencyCode, v.raw?.currency) || "")
          .trim()
          .toUpperCase();
        if (/^[A-Z]{3}$/.test(code)) codes.add(code);
      });
    } catch {
      /* fall through to the runtime currency list */
    }

    if (codes.size === 0 && typeof Intl.supportedValuesOf === "function") {
      try {
        Intl.supportedValuesOf("currency").forEach((c) => codes.add(c));
      } catch {
        /* no runtime currency list available */
      }
    }

    return Array.from(codes)
      .sort()
      .map((code) => ({ value: code, label: currencyLabel(code) }));
  });
}

/**
 * Approver options for "Approved By" (the API stores an employee id).
 * Sourced from HRMS GET /emp-info/search.
 */
export async function fetchApproverDropdownOptions({ size = 100 } = {}) {
  return dedupeAsync(`approverDropdown:${size}`, async () => {
    const response = await hrmsGet(
      `/emp-info/search?page=0&size=${size}&sortBy=empId&sortDir=ASC`,
      "HR01S"
    );
    const data = response?.data ?? response;
    const list = data?.content ?? (Array.isArray(data) ? data : []);

    return list
      .map((emp) => {
        const id = firstDefined(emp.empId, emp.employeeId, emp.emp_id, emp.id);
        if (id == null) return null;
        const name =
          firstDefined(
            emp.fullName,
            [emp.firstName, emp.lastName].filter(Boolean).join(" "),
            [emp.first_Name, emp.last_Name].filter(Boolean).join(" "),
            emp.name,
            emp.email
          ) || `Employee #${id}`;
        return { value: String(id), label: name };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  });
}

/** All Add/Edit Vendor dropdowns in one batch; a failed lookup yields an empty list. */
export async function fetchVendorFormLookups({ size = 100 } = {}) {
  const [statuses, riskLevels, currencies, approvers] = await Promise.allSettled([
    fetchVendorStatusDropdownOptions({ size: 1 }),
    fetchRiskLevelDropdownOptions({ size: 10 }),
    fetchCurrencyDropdownOptions({ size }),
    fetchApproverDropdownOptions({ size }),
  ]);

  const value = (result) => (result.status === "fulfilled" ? result.value || [] : []);
  return {
    // Fixed — no API call for Vendor Type.
    supplierTypeOptions: VENDOR_TYPE_OPTIONS,
    vendorStatusOptions: value(statuses),
    riskLevelOptions: value(riskLevels),
    currencyOptions: value(currencies),
    approverOptions: value(approvers),
  };
}

/** GET /product-vendors/search/pan */
export async function searchVendorByPan(pan) {
  const response = await imsGet(
    `/product-vendors/search/pan?pan=${encodeURIComponent(pan)}`,
    SEARCH
  );
  const item = unwrapItem(response);
  if (Array.isArray(item)) return item.map(mapApiVendorToUi).filter(Boolean);
  const mapped = mapApiVendorToUi(item);
  return mapped ? [mapped] : [];
}

/** GET /product-vendors/search/gstin */
export async function searchVendorByGstin(gstin) {
  const response = await imsGet(
    `/product-vendors/search/gstin?gstin=${encodeURIComponent(gstin)}`,
    SEARCH
  );
  const item = unwrapItem(response);
  if (Array.isArray(item)) return item.map(mapApiVendorToUi).filter(Boolean);
  const mapped = mapApiVendorToUi(item);
  return mapped ? [mapped] : [];
}

/** GET /product-vendors/count */
export async function fetchVendorCount() {
  const response = await imsGet("/product-vendors/count", SEARCH);
  return extractCount(response);
}

/** Active vendors for preferred-vendor dropdowns. */
export async function fetchActiveVendorsForDropdown({ size = 100 } = {}) {
  const page = await searchVendorsByStatus("Active", {
    page: 0,
    size,
    sortBy: "legalCompanyName",
    sortDir: "ASC",
  });
  return page.vendors || [];
}

/**
 * Manufacturer / OEM vendors for the Manufacturer (productBrand) dropdown.
 */
export async function fetchManufacturerVendorsForDropdown({ size = 100 } = {}) {
  const manufacturerTypes = SUPPLIER_TYPES.filter(
    (t) => t.label === "Manufacturer" || t.label === "OEM"
  );
  const results = await Promise.allSettled(
    manufacturerTypes.map((t) =>
      searchVendorsBySupplierType(t.id, {
        page: 0,
        size,
        sortBy: "legalCompanyName",
        sortDir: "ASC",
      })
    )
  );

  const byId = new Map();
  results.forEach((res) => {
    if (res.status !== "fulfilled") return;
    (res.value.vendors || []).forEach((v) => {
      if (v?.id != null) byId.set(String(v.id), v);
    });
  });

  if (byId.size === 0) {
    const active = await fetchActiveVendorsForDropdown({ size });
    active
      .filter((v) => {
        const type = String(v.type || "").toLowerCase();
        return type === "manufacturer" || type === "oem";
      })
      .forEach((v) => byId.set(String(v.id), v));
  }

  return Array.from(byId.values()).sort((a, b) =>
    String(a.tradeName || "").localeCompare(String(b.tradeName || ""))
  );
}

/** GET /product-vendors/count/status */
export async function fetchVendorCountByStatus(vendorStatus) {
  const status = toSearchVendorStatus(vendorStatus);
  const response = await imsGet(
    `/product-vendors/count/status?vendorStatus=${encodeURIComponent(status)}`,
    SEARCH
  );
  return extractCount(response);
}

/**
 * When the API response omits optional commercial/KYC fields that we just sent,
 * keep the request values so Add → View / Edit does not look empty.
 */
function mergeVendorUiWithRequest(mapped, body, form) {
  if (!mapped) return mapped;
  const fromBody = mapApiVendorToUi({ ...body, vendorId: mapped.vendorId ?? mapped.id }) || {};
  const fromFormDocs = Array.isArray(form?.uploadedFiles) ? form.uploadedFiles : null;

  return {
    ...fromBody,
    ...mapped,
    isoCertifications: mapped.isoCertifications || fromBody.isoCertifications || "",
    isAuthorized: mapped.isAuthorized || fromBody.isAuthorized || "",
    paymentTerms: mapped.paymentTerms || fromBody.paymentTerms || "",
    creditPeriod:
      mapped.creditPeriod !== undefined && mapped.creditPeriod !== null
        ? mapped.creditPeriod
        : fromBody.creditPeriod,
    uploadedFiles:
      mapped.uploadedFiles && mapped.uploadedFiles.length > 0
        ? mapped.uploadedFiles
        : fromBody.uploadedFiles && fromBody.uploadedFiles.length > 0
          ? fromBody.uploadedFiles
          : fromFormDocs || [],
  };
}

/** POST /product-vendors/add */
export async function createVendor(form) {
  const body = mapFormToApiPayload(form);
  const response = await imsPost("/product-vendors/add", body, ADD);
  const item = unwrapItem(response);
  const mapped = mapApiVendorToUi(item);
  let result;
  if (mapped?.id) {
    result = mergeVendorUiWithRequest(mapped, body, form);
  } else {
    const fallbackId = firstDefined(item?.vendorId, item?.vendor_id, item?.id);
    result =
      mergeVendorUiWithRequest(
        mapApiVendorToUi({ ...body, vendorId: fallbackId }),
        body,
        form
      ) || {
        id: fallbackId != null ? String(fallbackId) : undefined,
        ...form,
      };
  }
  saveVendorFieldOverlay(result?.id || result?.vendorId, result);
  return result;
}

/** PUT /product-vendors/update/{vendorId} */
export async function updateVendor(vendorId, form) {
  const body = mapFormToApiPayload(form);
  const response = await imsPut(`/product-vendors/update/${vendorId}`, body, UPDATE);
  const mapped = mapApiVendorToUi(unwrapItem(response));
  const result = mapped?.id
    ? mergeVendorUiWithRequest(mapped, body, form)
    : mergeVendorUiWithRequest(mapApiVendorToUi({ ...body, vendorId }), body, form) || {
        id: String(vendorId),
        ...form,
      };
  saveVendorFieldOverlay(result?.id || vendorId, result);
  return result;
}

/** PATCH /product-vendors/update/status/{vendorId} */
export async function updateVendorStatus(vendorId, vendorStatus) {
  const body = { vendorStatus: toApiVendorStatus(vendorStatus) };
  const response = await imsPatch(`/product-vendors/update/status/${vendorId}`, body, UPDATE);
  return (
    mapApiVendorToUi(unwrapItem(response)) || {
      id: String(vendorId),
      status: toUiVendorStatus(body.vendorStatus),
    }
  );
}

/** PATCH /product-vendors/approve/{vendorId} — approvedBy is an employee id (int32). */
export async function approveVendor(vendorId, { approvedBy, approvalDate } = {}) {
  const body = { approvedBy: toNumberOrNull(approvedBy) };
  const date = emptyToNull(approvalDate);
  if (date) body.approvalDate = date;
  const response = await imsPatch(`/product-vendors/approve/${vendorId}`, body, UPDATE);
  return mapApiVendorToUi(unwrapItem(response));
}

/** DELETE /product-vendors/delete/{vendorId} */
export async function deleteVendor(vendorId) {
  const response = await imsDel(`/product-vendors/delete/${vendorId}`, DELETE);
  return unwrapData(response);
}

/**
 * High-level list loader for the vendor catalog page.
 * Chooses the most specific search endpoint for server-side filtering.
 */
export async function loadVendorCatalog({
  searchQuery = "",
  filterType = "ALL",
  filterStatus = "ALL",
  filterRisk = "ALL",
  page = 0,
  size = 10,
} = {}) {
  const params = { page, size, sortBy: "legalCompanyName", sortDir: "ASC" };
  const q = searchQuery.trim();

  // Exact tax ID lookups
  if (q && /^[0-9A-Z]{15}$/i.test(q.replace(/\s/g, ""))) {
    const list = await searchVendorByGstin(q.replace(/\s/g, ""));
    return {
      content: list,
      vendors: list,
      totalElements: list.length,
      totalPages: 1,
      number: 0,
      size: list.length,
    };
  }
  if (q && /^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(q.replace(/\s/g, ""))) {
    const list = await searchVendorByPan(q.replace(/\s/g, "").toUpperCase());
    return {
      content: list,
      vendors: list,
      totalElements: list.length,
      totalPages: 1,
      number: 0,
      size: list.length,
    };
  }

  let result;

  if (q.includes("@")) {
    result = await searchVendorsByEmail(q, params);
  } else if (q) {
    result = await searchVendorsByTradeName(q, params);
    if ((result.content || []).length === 0) {
      result = await searchVendorsByLegalName(q, params);
    }
  } else if (filterType !== "ALL") {
    const typeId = resolveSupplierTypeId(filterType);
    result = typeId != null
      ? await searchVendorsBySupplierType(typeId, params)
      : await searchVendors(params);
  } else if (filterStatus !== "ALL") {
    result = await searchVendorsByStatus(filterStatus, params);
  } else if (filterRisk !== "ALL") {
    result = await searchVendorsByRiskLevel(filterRisk, params);
  } else {
    result = await searchVendors(params);
  }

  let vendors = result.content || [];

  // When search was primary, tighten remaining filters client-side on the page result.
  if (q) {
    if (filterType !== "ALL") {
      vendors = vendors.filter(
        (v) => String(v.type || "").toLowerCase() === String(filterType).toLowerCase()
      );
    }
  }

  // Always apply Status locally. The dedicated search endpoint may ignore param
  // casing or return mixed rows, and combined filters never used it as the
  // primary criterion when Type or Risk is also selected.
  if (filterStatus !== "ALL") {
    const wantedStatus = toUiVendorStatus(filterStatus);
    vendors = vendors.filter((v) => toUiVendorStatus(v.status) === wantedStatus);
  }

  // Always apply Risk Level locally. The dedicated search endpoint may ignore
  // param casing or return mixed rows, and combined filters never used it as
  // the primary criterion.
  if (filterRisk !== "ALL") {
    const wantedRisk = toUiRiskLevel(filterRisk);
    vendors = vendors.filter((v) => toUiRiskLevel(v.riskLevel) === wantedRisk);
  }

  const filteredCount = vendors.length;
  const originalCount = (result.content || []).length;
  const totalsChanged = filteredCount !== originalCount;

  return {
    ...result,
    content: vendors,
    vendors,
    totalElements: totalsChanged ? filteredCount : result.totalElements,
    totalPages: totalsChanged
      ? Math.max(1, Math.ceil(filteredCount / (size || 10)))
      : result.totalPages,
  };
}
